import {
  ButtonGroup as MuiButtonGroup,
  ButtonGroupTypeMap as MuiButtonGroupTypeMap,
  ButtonGroupProps as MuiButtonGroupProps,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { OverrideProps } from '@mui/material/OverridableComponent';
import clsx from 'clsx';
import * as React from 'react';
import ButtonGroupContext, {
  IButtonGroupContext,
} from 'src/components/ButtonGroup/ButtonGroupContext';
import {
  ExtendAndOmitTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';
import { unifyButtonGroupDefaultProps } from 'src/components/ButtonGroup/unifyButtonGroupDefaultProps';
import { wsdButtonGroupDefaultProps } from 'src/wsd/components/ButtonGroup/wsdButtonGroupDefaultProps';
import shouldDisableButtonElevation from 'src/wsd/components/Button/shouldDisableButtonElevation';
import WsdButtonGroupRoot from 'src/wsd/components/ButtonGroup/WsdButtonGroupRoot';
import resolveProps from 'src/wsd/components/utils/resolveProps';
import useUnifyThemeOrStaticLegacyTheme from 'src/wsd/utils/useUnifyThemeOrStaticLegacyTheme';
import { legacyWsdStyleSupportClassName } from 'src/wsd/utils/classNameHelpers';

// Module augmentation for size prop
// See: <https://next.material-ui.com/customization/theme-components/#adding-new-component-variants>
declare module '@mui/material/ButtonGroup' {
  interface ButtonGroupPropsSizeOverrides {
    // WSD Buttons Only
    xxsmall: true;
    // WSD & Unify Buttons
    xsmall: true;
  }
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ButtonGroupOwnProps {
  /**
   * The variant to use.
   * @default 'contained'
   */
  variant?: MuiButtonGroupProps['variant'];
}

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  disableElevation?: MuiButtonProps['disableElevation'];
};

export type ButtonGroupTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiButtonGroupTypeMap['defaultComponent']
> = ExtendAndOmitTypeMap<
  MuiButtonGroupTypeMap,
  AdditionalProps & ButtonGroupOwnProps & UnsupportedProps,
  | keyof ButtonGroupOwnProps
  | keyof UnsupportedProps
  | 'disableFocusRipple'
  | 'disableRipple',
  RootComponent
>;

export type ButtonGroupProps<
  RootComponent extends React.ElementType = MuiButtonGroupTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  ButtonGroupTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

/**
 * The `ButtonGroup` component can be used to group related {@link Button}s.
 */
export const ButtonGroup = forwardRefToOverridableComponent<
  ButtonGroupTypeMap,
  ButtonGroupProps
>(function ButtonGroup(inProps, forwardedRef) {
  const { theme, legacyWsdStyleSupport } = useUnifyThemeOrStaticLegacyTheme();
  const {
    disableElevation: disableElevationDefault, // eslint-disable-line deprecation/deprecation
    ...defaultProps
  } = legacyWsdStyleSupport
    ? wsdButtonGroupDefaultProps
    : unifyButtonGroupDefaultProps;
  const {
    className,
    color,
    size = 'medium',
    variant,
    fullWidth,
    disabled,
    disableElevation: disableElevationProp, // eslint-disable-line deprecation/deprecation
    ...rest
  } = resolveProps<ButtonGroupProps>(defaultProps, inProps);
  const classes = clsx(
    legacyWsdStyleSupport && legacyWsdStyleSupportClassName,
    className
  );

  let safeColor = color;
  if (!legacyWsdStyleSupport && color?.startsWith('wsdBtn')) {
    // When we switch away from using these deprecated variants, make
    // any that still exist just look like whatever the default color is
    // in the non-legacy theme.
    safeColor = undefined;
  }

  const disableElevation = shouldDisableButtonElevation(
    disableElevationProp,
    disableElevationDefault,
    theme,
    legacyWsdStyleSupport,
    size
  );

  // MUI's `MuiButtonGroup` uses a private context to pass some of its props,
  // like color, variant, outline, etc. to descendant `MuiButton`s.
  //
  // Since those values set in that private context, our wrapper `Button` isn't able to access them.
  // This results in any styling that relies on those values not being applied
  // (e.g., a descendant Button not getting the color of its group).
  //
  // In order for our `Button` wrapper to be styled correctly inside a `ButtonGroup`,
  // we had to create our own context to pass those details to our `Button`.
  const context = React.useMemo<IButtonGroupContext>(
    () => ({
      color,
      disabled,
      disableElevation,
      fullWidth,
      size,
      variant,
    }),
    [color, disabled, disableElevation, fullWidth, size, variant]
  );

  const StyledRoot = legacyWsdStyleSupport
    ? WsdButtonGroupRoot
    : MuiButtonGroup;

  return (
    <ButtonGroupContext.Provider value={context}>
      <StyledRoot
        className={classes}
        ref={forwardedRef}
        color={safeColor}
        size={size}
        variant={variant}
        fullWidth={fullWidth}
        disabled={disabled}
        disableElevation={disableElevation}
        {...rest}
      />
    </ButtonGroupContext.Provider>
  );
});

export default ButtonGroup;
